import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Pathnames visited in this page session. Kept outside React so it survives the
// remounts a filter change causes (Content bumps the subtree key).
let visited: string[] = [];

const recordPathname = (pathname: string) => {
    if (visited[visited.length - 1] === pathname) return;
    visited.push(pathname);
    if (visited.length > 5) visited.shift();
};

const pathnameBefore = (pathname: string): string | undefined => {
    for (let index = visited.length - 1; index >= 0; index--) {
        if (visited[index] !== pathname) return visited[index];
    }
    return undefined;
};

const isWithin = (pathname: string, recordPath: string) =>
    pathname === recordPath || pathname.startsWith(`${recordPath}/`);

// Mounted once for the whole app so the trail has no gaps while the user is on
// pages that do not have tabs.
export const TabLandingTracker = () => {
    const location = useLocation();

    useEffect(() => {
        recordPathname(location.pathname);
    }, [location.pathname]);

    return null;
};

interface TabLandingOptions {
    // Identifies the record being viewed, e.g. /instances/101. Navigation that stays
    // inside it — tab clicks, breadcrumbs — is left alone.
    recordPath?: string;
    // The tab a record opens on today, e.g. /instances/101/activity.
    landingPath?: string;
    // Resolves the tab to land on instead, or undefined to stay put.
    resolveTab: () => string | undefined;
    // Hold off until the data that decides which tabs exist has loaded.
    ready?: boolean;
}

// Resolves the tab to land on during render, or undefined to stay put. Views that can
// hold back their routes use this with <Navigate>, so the default tab never mounts and
// never fires its requests.
export const useTabLandingTarget = ({ recordPath, landingPath, resolveTab, ready = true }: TabLandingOptions): string | undefined => {
    const location = useLocation();

    if (!ready || !recordPath || !landingPath) return undefined;
    if (location.pathname !== landingPath) return undefined;

    const previous = pathnameBefore(location.pathname);
    if (previous && isWithin(previous, recordPath)) return undefined;

    const target = resolveTab();
    if (!target || target === location.pathname) return undefined;

    return `${target}${location.search}`;
};

// Same decision, applied by navigating. For views whose tab content is mounted by a
// parent route and so cannot be held back.
export const useTabLanding = (options: TabLandingOptions) => {
    const navigate = useNavigate();
    const target = useTabLandingTarget(options);

    useEffect(() => {
        if (target) navigate(target, { replace: true });
    }, [target, navigate]);
};
